import React from 'react';
import styled from '@emotion/styled';
import { calculateTimeToRead } from 'utils/calculateTimeToRead';
import { Colors, Typography } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { ArticleHeroSocialIcons } from './ArticleHeroSocialIcons';
import { Article, Author } from 'data/SingleArticle/types';
import { CommonLinkText, LargeBlackText } from 'components/typography';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { Link } from 'components/Link/Link';
import { ImageQueryParams } from 'components/Images/RichImageComponent/RichImageComponent';
import {
  ArticleImage,
  defaultImageQueryParams,
} from 'components/Images/ArticleImage/ArticleImage';

type Props = {
  article: Article;
};

const heroImageQueryParams: Readonly<ImageQueryParams> = {
  ...defaultImageQueryParams,
  w: '1520',
  h: '760',
};

const AuthorName = ({ authors }: { authors: Author[] }) => {
  if (authors.length === 1) {
    return (
      <>
        <span>{authors[0].name}</span>
        <span> &#183; </span>
      </>
    );
  }

  if (authors.length === 2) {
    return (
      <>
        <span>{authors[0].name}</span>
        <span> and </span>
        <span>{authors[1].name}</span>
        <span> &#183; </span>
      </>
    );
  }

  return (
    <>
      {authors.map((author, index) => (
        <span key={`author-hero-${index}`}>
          <span>
            {author.name}
            {index === authors.length - 1 ? <span> &#183; </span> : ', '}
          </span>
        </span>
      ))}
    </>
  );
};

export const ArticleHero = ({ article }: Props) => {
  const {
    categoryAssemblyTitle,
    categoryAssemblyHref,
    timeToRead,
    title,
    subtitle,
    author: authors,
    image,
  } = article;
  const trackEvent = useTrackEvent();

  return (
    <section>
      <CategoryWrapper>
        <Link
          href={article.categoryAssemblyHref}
          onClick={(event) =>
            trackEvent('linkClicked', {
              linkText: categoryAssemblyTitle,
              href: categoryAssemblyHref,
              path: window.location.pathname,
              posYFromTop: event.clientY,
            })
          }
        >
          <CommonLinkText>{article.categoryAssemblyTitle}</CommonLinkText>
        </Link>
        <TimeToRead>{calculateTimeToRead(timeToRead)}</TimeToRead>
      </CategoryWrapper>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
      <AuthorWrapper>
        By {<AuthorName authors={authors} />}
        {article.editorialLastPublishedDateComputed}
      </AuthorWrapper>
      <SocialIcons />
      {image.url && (
        <Figure>
          <ArticleImage image={image} queryParams={heroImageQueryParams} />
        </Figure>
      )}
    </section>
  );
};

const AuthorWrapper = styled.div`
  color: ${Colors.neutral700};
  margin-top: 20px;
  text-align: center;
  ${Typography.smallPrint1};
`;

const CategoryWrapper = styled.div`
  align-items: center;
  font-size: 14px;
  display: grid;
  justify-content: center;
  grid-template-columns: auto auto;
  grid-column-gap: 10px;
`;

const Figure = styled.figure`
  margin: 20px auto 0 auto;
  ${mediaQueries.desktop} {
    max-width: 768px;
  }
`;

const SocialIcons = styled(ArticleHeroSocialIcons)`
  margin-top: 30px;
  ${mediaQueries.tablet} {
    margin-top: 20px;
  }
`;

const Subtitle = styled.h2`
  color: ${Colors.neutral700};
  text-align: center;
  margin: 20px 0 0 0;
  ${Typography.paragraph1Short}
  ${mediaQueries.tablet} {
    ${Typography.subHeader1}
  }
`;

const TimeToRead = styled.span`
  color: ${Colors.neutral700};
  ${Typography.smallPrint1};
`;

const Title = styled(LargeBlackText)`
  text-align: center;
  margin: 15px auto 0 auto;
  ${mediaQueries.tablet} {
    max-width: 768px;
  }
`;
